import {CushlaGetQueryOptions, endpointKeys, getFetchFn} from '@common/api'
import type {
  UpsertPartnerServiceRequest,
  PartnerServiceResponse,
  QuestionnaireShortDto,
} from '@generated/models'
import {
  partnerServiceNormaliser,
  partnerServicesNormaliser,
  staticFileNormaliser,
} from '@common/normalisers'
import {useCallback, useState} from 'react'

import {
  downloadFile,
  useApi,
  useHeaders,
  useWebGet,
  useWebSet,
  useWebSetFormData,
} from './helpers'

/**
 * Request body for multipart create/update. The OpenAPI model types attachmentFileId
 * as optional string only; we also allow null so JSON can send explicit null to clear
 * an attachment on update.
 */
export type PartnerServiceUpsertRequestBody = Omit<
  UpsertPartnerServiceRequest,
  'attachmentFileId'
> & {
  /** Omit: server default; string: keep current file id on update; null: clear attachment. */
  attachmentFileId?: string | null
}

export type UpsertPartnerServiceWithOptionalFilePayload = {
  body: PartnerServiceUpsertRequestBody
  file?: File
}

function buildPartnerServiceMultipartFormData(
  payload: UpsertPartnerServiceWithOptionalFilePayload,
): FormData {
  const formData = new FormData()
  formData.append(
    'body',
    new Blob([JSON.stringify(payload.body)], {
      type: 'application/json',
    }),
  )
  if (payload.file) {
    formData.append('file', payload.file)
  }
  return formData
}

export const useGetPartnerServices = (
  searchTerm?: string,
  options?: CushlaGetQueryOptions<PartnerServiceResponse[]>,
) => {
  const api = useApi()
  const trimmed = searchTerm?.trim()
  return useWebGet<PartnerServiceResponse[]>(
    [endpointKeys.partnerAdminPartnerServices, {searchTerm: trimmed ?? ''}],
    api.partnerAdminPartnerServices(
      trimmed ? {searchTerm: trimmed} : undefined,
    ),
    partnerServicesNormaliser,
    options,
  )
}

export const useDownloadPartnerServiceAttachment = () => {
  const api = useApi()
  const getHeader = useHeaders()
  const [loadingServiceId, setLoadingServiceId] = useState<string | null>(null)

  const downloadAttachment = useCallback(
    async (serviceId: string) => {
      setLoadingServiceId(serviceId)
      try {
        const endpoint = api.partnerAdminPartnerServiceAttachment({
          id: serviceId,
        })
        const file = await getFetchFn(
          getHeader,
          endpoint,
          staticFileNormaliser,
        )()
        await downloadFile(new URL(file.url), async () => ({}), 'attachment', {
          fallbackToAnchorOnFailure: true,
        })
      } finally {
        setLoadingServiceId(null)
      }
    },
    [api, getHeader],
  )

  return {downloadAttachment, loadingServiceId}
}

export const useCreatePartnerService = () =>
  useWebSetFormData<
    UpsertPartnerServiceWithOptionalFilePayload,
    PartnerServiceResponse
  >(
    [endpointKeys.partnerAdminPartnerServices],
    useApi().partnerAdminPartnerServices(undefined),
    'POST',
    partnerServiceNormaliser,
    buildPartnerServiceMultipartFormData,
    [[endpointKeys.partnerAdminPartnerServices]],
  )

export const useUpdatePartnerService = (id: string) =>
  useWebSetFormData<
    UpsertPartnerServiceWithOptionalFilePayload,
    PartnerServiceResponse
  >(
    [endpointKeys.partnerAdminPartnerService, {id}],
    useApi().partnerAdminPartnerService({id}),
    'PUT',
    partnerServiceNormaliser,
    buildPartnerServiceMultipartFormData,
    [[endpointKeys.partnerAdminPartnerServices]],
  )

export const useDeletePartnerService = (id: string) =>
  useWebSet<void, void>(
    [endpointKeys.partnerAdminPartnerService, {id}],
    useApi().partnerAdminPartnerService({id}),
    'DELETE',
    () => undefined,
    [[endpointKeys.partnerAdminPartnerServices]],
  )

// Questionnaires list for combobox (global list)
export const useGetQuestionnairesShort = (facilityId?: string) =>
  useWebGet<QuestionnaireShortDto[]>(
    [endpointKeys.questionnaires, {facilityId: facilityId || ''}],
    useApi().questionnaires({facilityId: facilityId || ''}),
    (data) => data,
  )
